import React, { useMemo } from 'react';
import {
  useGetAdvisorGroupSettingsQuery,
  AdvisorGroupSetting,
} from '@/store/settingsApi';
import { ExternalLink } from '@/components/ExternalLink';

export interface TickerUrlFormatterOptions {
  excludedTickers?: string[];
  maxTickerLength?: number;
  iconFontSize?: string;
  iconStrokeWidth?: number;
}

export interface UseTickerUrlFormatterReturn {
  tickerUrlFormatter: (value: any) => React.ReactNode | string;
  isLoading: boolean;
  tickerUrl: string;
}

const DEFAULT_EXCLUDED_TICKERS = ['cash', 'amgxx', 'ammxx'];
const DEFAULT_MAX_TICKER_LENGTH = 5;

/**
 * Custom hook that provides a ticker URL formatter for creating clickable ticker symbols.
 *
 * @param options - Configuration options for the formatter
 * @param options.excludedTickers - Array of ticker symbols to exclude from linking (default: ["cash", "amgxx", "ammxx"])
 * @param options.maxTickerLength - Maximum length of ticker symbol to allow linking (default: 5)
 * @param options.iconFontSize - Font size for the check icon (default: "1rem")
 * @param options.iconStrokeWidth - Stroke width for the check icon (default: 1.5)
 * @returns Object containing the formatter function, loading state, and ticker URL
 */
export const useTickerUrlFormatter = (
  options: TickerUrlFormatterOptions = {},
): UseTickerUrlFormatterReturn => {
  const {
    excludedTickers = DEFAULT_EXCLUDED_TICKERS,
    maxTickerLength = DEFAULT_MAX_TICKER_LENGTH,
    iconFontSize = '1rem',
    iconStrokeWidth = 1.5,
  } = options;

  const { data: advisorGroupSettings, isLoading } =
    useGetAdvisorGroupSettingsQuery();

  const tickerUrl = useMemo(() => {
    const settings = (advisorGroupSettings || []) as AdvisorGroupSetting[];
    const urlSetting = settings.find((s) => s.Key === 'TickerUrl');
    return urlSetting?.Value || '';
  }, [advisorGroupSettings]);

  const tickerUrlFormatter = useMemo(() => {
    return (value: any) => {
      if (!value) return '';

      const ticker = String(value).trim();
      const url = tickerUrl;

      // Check all conditions that would prevent linking
      if (
        !ticker ||
        !ticker.length ||
        ticker.length > maxTickerLength ||
        !url.length ||
        excludedTickers.includes(ticker.toLowerCase())
      ) {
        return ticker;
      }

      // Create the URL by replacing the ticker placeholder
      const finalUrl = url.replace('{ticker}', ticker);

      return <ExternalLink url={finalUrl}>{ticker}</ExternalLink>;
    };
  }, [
    tickerUrl,
    excludedTickers,
    maxTickerLength,
    iconFontSize,
    iconStrokeWidth,
  ]);

  return {
    tickerUrlFormatter,
    isLoading,
    tickerUrl,
  };
};
